import React, { useState, useEffect, useRef } from 'react';
import { Heart, Users, Eye, Mail, Bell, ChevronRight, CheckCircle2 } from 'lucide-react';
import { gsap } from 'gsap';
import api from '../../services/api';

const Home = ({ setActiveTab }) => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // GSAP Refs
    const containerRef = useRef(null);
    const heroRef = useRef(null);
    const statsRef = useRef([]);
    const matchesRef = useRef(null);
    const notifRef = useRef(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await api.get('/dashboard/home');
                setDashboardData(response.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    // GSAP Animations
    useEffect(() => {
        if (!loading && dashboardData) {
            const tl = gsap.timeline();
            
            // Hero Banner Animation
            tl.fromTo(heroRef.current, 
                { y: 30, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
            );

            // Stats Stagger Animation
            tl.fromTo(statsRef.current,
                { y: 40, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: "back.out(1.2)" },
                "-=0.4"
            );

            // Matches & Notifications Fade-Up
            tl.fromTo([matchesRef.current, notifRef.current],
                { y: 40, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, stagger: 0.2, ease: "power3.out" },
                "-=0.2"
            );
        }
    }, [loading, dashboardData]);

    const getProfileImage = (img, gender) => {
        if (!img) return gender === 'Female' ? 'https://randomuser.me/api/portraits/women/85.jpg' : 'https://randomuser.me/api/portraits/men/85.jpg';
        if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('blob:')) return img;
        const BASE_URL = api.defaults.baseURL || "";
        return `${BASE_URL.replace("/api", "")}${img.startsWith('/') ? img : `/${img}`}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 border-t-4 border-primary-pink border-solid rounded-full animate-spin"></div>
                    <div className="absolute inset-2 border-t-4 border-accent-cyan border-solid rounded-full animate-spin animation-delay-150"></div>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="text-center py-20 text-text-secondary">
                <p>Unable to load dashboard data</p>
            </div>
        );
    }

    const stats = [
        { title: "NEW MATCHES", count: dashboardData.stats.newMatches || 0, icon: Heart, color: "text-primary-pink", bgColor: "bg-primary-pink/10", glow: "shadow-sm" },
        { title: "PROFILE VIEWS", count: dashboardData.stats.profileViews || 0, icon: Eye, color: "text-primary-pink", bgColor: "bg-primary-pink/10", glow: "shadow-sm" },
        { title: "INTERESTS", count: dashboardData.stats.interestsReceived || 0, icon: Users, color: "text-primary-pink", bgColor: "bg-primary-pink/10", glow: "shadow-sm" },
        { title: "MESSAGES", count: dashboardData.stats.unreadMessages || 0, icon: Mail, color: "text-success-green", bgColor: "bg-success-green/10", glow: "shadow-sm" }
    ];

    return (
        <div ref={containerRef} className="space-y-6 w-full mx-auto p-4 lg:p-8 relative min-h-screen z-10">
            {/* Ambient Background Elements (only contained in Home) */}
            <div className="fixed top-20 right-40 w-96 h-96 bg-glow-pink blur-[120px] rounded-full mix-blend-screen pointer-events-none -z-10 animate-float" />
            <div className="fixed bottom-20 left-40 w-[30rem] h-[30rem] bg-glow-purple blur-[150px] rounded-full mix-blend-screen pointer-events-none -z-10 animate-blob" />

            {/* Hero Banner Wrapper */}
            <div ref={heroRef}>
                {/* Light Mode Banner */}
                <div className="dark:hidden relative overflow-hidden bg-gradient-to-r from-[#EC5B55] via-[#FF9292] to-[#FFD5D5] rounded-[24px] p-8 md:p-12 text-white shadow-sm border border-white/40 mb-6">
                    
                    {/* Elegant Wave Pattern (Light Mode) */}
                    <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none opacity-40">
                        <svg className="relative block w-[200%] h-[120px]" style={{ transform: 'translateX(-10%)' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,121.31,192.4,110.4,236.4,102.6,280.05,82.46,321.39,56.44Z" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5"></path>
                            <path d="M321.39,66.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,41,906.67,82,985.66,102.83c70.05,18.48,146.53,26.09,214.34,13V120H0V105.8C59.71,128.08,130.83,131.31,192.4,120.4,236.4,112.6,280.05,92.46,321.39,66.44Z" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5"></path>
                            <path d="M321.39,76.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,51,906.67,92,985.66,112.83c70.05,18.48,146.53,26.09,214.34,23V120H0V115.8C59.71,138.08,130.83,141.31,192.4,130.4,236.4,122.6,280.05,102.46,321.39,76.44Z" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"></path>
                        </svg>
                    </div>

                    {/* 3D Glossy Hearts (Light Mode) */}
                    <div className="absolute right-10 md:right-32 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center animate-float">
                        <svg width="0" height="0">
                            <defs>
                                <linearGradient id="light-heart-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#FFA6A8" />
                                    <stop offset="50%" stopColor="#F9656B" />
                                    <stop offset="100%" stopColor="#E0545A" />
                                </linearGradient>
                            </defs>
                        </svg>
                        
                        {/* Main large heart */}
                        <div className="relative">
                            <Heart size={160} fill="url(#light-heart-gradient)" stroke="none" className="drop-shadow-[0_15px_25px_rgba(236,91,85,0.4)] z-20 relative" style={{ filter: 'drop-shadow(0 0 15px rgba(255,255,255,0.5))' }} />
                            
                            {/* Highlight reflex for 3D effect */}
                            <div className="absolute top-[20%] left-[25%] w-[30%] h-[30%] bg-white/60 rounded-full blur-[8px] z-30 pointer-events-none transform -rotate-45"></div>

                            {/* Small floating heart 1 (Top Right) */}
                            <div className="absolute -top-10 -right-16 z-10 animate-pulse" style={{ animationDuration: '3s' }}>
                                <Heart size={50} fill="url(#light-heart-gradient)" stroke="none" className="drop-shadow-[0_10px_15px_rgba(236,91,85,0.3)]" />
                                <div className="absolute top-[20%] left-[25%] w-[30%] h-[30%] bg-white/60 rounded-full blur-[3px] pointer-events-none transform -rotate-45"></div>
                            </div>

                            {/* Small floating heart 2 (Bottom Right) */}
                            <div className="absolute -bottom-8 -right-4 z-30 animate-bounce" style={{ animationDuration: '4s' }}>
                                <Heart size={35} fill="url(#light-heart-gradient)" stroke="none" className="drop-shadow-[0_8px_12px_rgba(236,91,85,0.3)]" />
                                <div className="absolute top-[20%] left-[25%] w-[30%] h-[30%] bg-white/60 rounded-full blur-[2px] pointer-events-none transform -rotate-45"></div>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-40 max-w-2xl">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight drop-shadow-md flex items-center gap-3">
                            Welcome back, {dashboardData.user.name || "Guest"}! <span className="animate-wave inline-block origin-bottom-right">👋</span>
                        </h1>
                        <p className="text-lg text-white/90 mb-8 font-medium drop-shadow-sm">
                            You have {dashboardData.stats.newMatches} new match{dashboardData.stats.newMatches !== 1 ? 'es' : ''} waiting for you today.
                        </p>
                        <button 
                            onClick={() => setActiveTab('matches')}
                            className="group flex items-center gap-2 px-8 py-3 bg-[#F9656B] hover:bg-[#E0545A] text-white rounded-full font-bold transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 border border-white/20"
                        >
                            View Matches 
                        </button>
                    </div>
                </div>

                {/* Dark Mode Banner (Original) */}
                <div className="hidden dark:block relative overflow-hidden bg-gradient-to-r from-rose-500 via-purple-600 to-indigo-700 rounded-[24px] p-8 md:p-12 text-white shadow-[0_20px_50px_rgba(139,92,246,0.3)] border border-white/10 mb-6">
                    
                    {/* Twinkling Stars */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/4 left-1/4 w-1.5 h-1.5 bg-white rounded-full animate-ping opacity-70"></div>
                        <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-white rounded-full animate-pulse opacity-80 shadow-[0_0_10px_white]"></div>
                        <div className="absolute bottom-1/3 right-1/3 w-1.5 h-1.5 bg-white rounded-full animate-ping opacity-60"></div>
                        <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-pink-300 rounded-full animate-pulse opacity-80 shadow-[0_0_15px_#FF2E88]"></div>
                        <div className="absolute top-1/5 right-1/12 w-1 h-1 bg-white rounded-full animate-ping opacity-90 shadow-[0_0_10px_white]"></div>
                    </div>

                    {/* Elegant Wave Pattern */}
                    <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none opacity-20">
                        <svg className="relative block w-[200%] h-[120px]" style={{ transform: 'translateX(-10%)' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C59.71,118.08,130.83,121.31,192.4,110.4,236.4,102.6,280.05,82.46,321.39,56.44Z" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1"></path>
                            <path d="M321.39,66.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,41,906.67,82,985.66,102.83c70.05,18.48,146.53,26.09,214.34,13V120H0V105.8C59.71,128.08,130.83,131.31,192.4,120.4,236.4,112.6,280.05,92.46,321.39,66.44Z" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1"></path>
                            <path d="M321.39,76.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,51,906.67,92,985.66,112.83c70.05,18.48,146.53,26.09,214.34,23V120H0V115.8C59.71,138.08,130.83,141.31,192.4,130.4,236.4,122.6,280.05,102.46,321.39,76.44Z" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1"></path>
                            <path d="M321.39,86.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,61,906.67,102,985.66,122.83c70.05,18.48,146.53,26.09,214.34,33V120H0V125.8C59.71,148.08,130.83,151.31,192.4,140.4,236.4,132.6,280.05,112.46,321.39,86.44Z" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1"></path>
                        </svg>
                    </div>

                    {/* 3D Glossy Hearts */}
                    <div className="absolute right-10 md:right-24 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center animate-float">
                        <svg width="0" height="0">
                            <defs>
                                <linearGradient id="glossy-heart-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#FF9EC6" />
                                    <stop offset="30%" stopColor="#FF2E88" />
                                    <stop offset="70%" stopColor="#D900FF" />
                                    <stop offset="100%" stopColor="#4F46E5" />
                                </linearGradient>
                            </defs>
                        </svg>
                        
                        {/* Main large heart */}
                        <div className="relative">
                            <Heart size={180} fill="url(#glossy-heart-gradient)" stroke="none" className="drop-shadow-[0_20px_30px_rgba(0,0,0,0.5)] z-20 relative" style={{ filter: 'drop-shadow(0 0 20px rgba(255,46,136,0.6))' }} />
                            
                            {/* Highlight reflex for 3D effect */}
                            <div className="absolute top-[20%] left-[25%] w-[30%] h-[30%] bg-white/40 rounded-full blur-[10px] z-30 pointer-events-none transform -rotate-45"></div>

                            {/* Small floating heart 1 */}
                            <div className="absolute -bottom-6 -right-10 z-30 animate-bounce" style={{ animationDuration: '4s' }}>
                                <Heart size={70} fill="url(#glossy-heart-gradient)" stroke="none" className="drop-shadow-[0_10px_20px_rgba(0,0,0,0.4)]" />
                                <div className="absolute top-[20%] left-[25%] w-[30%] h-[30%] bg-white/40 rounded-full blur-[4px] pointer-events-none transform -rotate-45"></div>
                            </div>

                            {/* Small floating heart 2 */}
                            <div className="absolute top-4 -right-16 z-10 animate-pulse" style={{ animationDuration: '3s' }}>
                                <Heart size={40} fill="url(#glossy-heart-gradient)" stroke="none" className="drop-shadow-[0_10px_15px_rgba(0,0,0,0.3)]" />
                                <div className="absolute top-[20%] left-[25%] w-[30%] h-[30%] bg-white/40 rounded-full blur-[2px] pointer-events-none transform -rotate-45"></div>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-40 max-w-2xl">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight drop-shadow-md">
                            Welcome back, {dashboardData.user.name || "Guest"}! 👋
                        </h1>
                        <p className="text-lg text-white/90 mb-8 font-medium drop-shadow-sm">
                            You have {dashboardData.stats.newMatches} new match{dashboardData.stats.newMatches !== 1 ? 'es' : ''} waiting for you today.
                        </p>
                        <button 
                            onClick={() => setActiveTab('matches')}
                            className="group flex items-center gap-2 px-8 py-3.5 bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/40 rounded-full font-bold transition-all duration-300 shadow-[0_8px_32px_rgba(255,255,255,0.2)] text-white"
                        >
                            View Matches 
                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <div 
                        key={index} 
                        ref={el => statsRef.current[index] = el}
                        className="relative overflow-hidden bg-white dark:bg-glass-card backdrop-blur-md border border-gray-100 dark:border-glass-border p-6 rounded-2xl hover:-translate-y-1 transition-all duration-300 shadow-sm"
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${stat.bgColor} ${stat.glow} mb-4 transition-transform duration-500`}>
                                <stat.icon className={`w-6 h-6 ${stat.color}`} />
                            </div>
                            
                            <div className="mt-2">
                                <h3 className="text-3xl font-bold text-gray-900 dark:text-white drop-shadow-sm leading-none tracking-tight">
                                    {stat.count}
                                </h3>
                                <p className="text-xs font-semibold text-gray-500 dark:text-text-muted mt-2 tracking-widest uppercase">
                                    {stat.title}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Matches & Notifications Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Recent Matches */}
                <div ref={matchesRef} className="xl:col-span-2 bg-glass-card backdrop-blur-[20px] border border-glass-border rounded-[24px] p-6 md:p-8 relative shadow-glass overflow-hidden">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="text-xl font-bold text-text-primary tracking-wide drop-shadow-sm">Recent Matches</h3>
                        <button onClick={() => setActiveTab('matches')} className="text-primary-pink text-sm font-bold uppercase tracking-wider hover:text-accent-cyan transition-colors flex items-center gap-1">
                            View All <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    {dashboardData.recentMatches.length === 0 ? (
                        <div className="text-center py-12 text-text-muted">
                            <Heart className="w-12 h-12 mx-auto mb-4 opacity-20" />
                            <p>No matches available yet</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {dashboardData.recentMatches.map(profile => (
                                <div key={profile.id || profile._id} className="relative flex items-center p-4 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-2xl hover:bg-[rgba(255,255,255,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group shadow-glass-sm">
                                    <div className="relative">
                                        <img
                                            src={getProfileImage(profile.img, profile.gender)}
                                            alt={profile.name}
                                            className="w-16 h-16 rounded-full object-cover border-[3px] border-dark-navy shadow-depth group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-success-green border-[3px] border-dark-navy rounded-full"></div>
                                    </div>

                                    <div className="ml-4 flex-1">
                                        <h4 className="font-bold text-text-primary truncate flex items-center gap-2">
                                            {profile.name}
                                            <CheckCircle2 className="w-4 h-4 text-success-green" />
                                        </h4>
                                        <p className="text-xs text-text-secondary mt-1">
                                            {profile.age ? `${profile.age} yrs, ` : ''}{profile.location || 'Not specified'}
                                        </p>
                                        
                                        <div className="flex gap-2 mt-3">
                                            <span className="px-2 py-1 bg-[rgba(255,255,255,0.05)] border border-glass-border rounded-md text-[10px] text-text-secondary tracking-wider">
                                                {profile.religion || "Hindu"}
                                            </span>
                                            <span className="px-2 py-1 bg-[rgba(255,255,255,0.05)] border border-glass-border rounded-md text-[10px] text-text-secondary tracking-wider">
                                                {profile.education || "B.E"}
                                            </span>
                                        </div>

                                        {/* Circular Percentage Progress */}
                                        {profile.matchScore && (
                                            <div className="absolute top-1/2 right-4 -translate-y-1/2 flex flex-col items-center">
                                                <div className="relative w-12 h-12">
                                                    <svg className="w-full h-full transform -rotate-90">
                                                        <circle cx="24" cy="24" r="20" stroke="rgba(255,46,136,0.2)" strokeWidth="3" fill="none" />
                                                        <circle cx="24" cy="24" r="20" stroke="#FF2E88" strokeWidth="3" fill="none" strokeDasharray="125.6" strokeDashoffset={`${125.6 - (125.6 * profile.matchScore) / 100}`} className="transition-all duration-1000 ease-out" />
                                                    </svg>
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <span className="text-[10px] font-bold text-primary-pink drop-shadow-sm">{profile.matchScore}%</span>
                                                    </div>
                                                </div>
                                                <span className="text-[9px] text-primary-pink mt-1 uppercase font-bold tracking-widest drop-shadow-sm">Match</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Notifications Panel */}
                <div ref={notifRef} className="bg-glass-card backdrop-blur-[20px] border border-glass-border rounded-[24px] p-6 md:p-8 flex flex-col h-full shadow-glass relative overflow-hidden">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="text-xl font-bold text-text-primary tracking-wide drop-shadow-sm">Notifications</h3>
                        <button className="text-text-muted hover:text-text-primary transition-colors">
                            <Bell className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-4">
                        {dashboardData.notifications.length === 0 ? (
                            <div className="text-center py-12 text-text-muted h-full flex flex-col items-center justify-center">
                                <Bell className="w-10 h-10 mb-3 opacity-20" />
                                <p className="text-sm">No notifications yet</p>
                            </div>
                        ) : (
                            dashboardData.notifications.map((notification, index) => (
                                <div key={index} className="flex items-center space-x-4 p-4 bg-[rgba(255,255,255,0.03)] rounded-2xl hover:bg-[rgba(255,255,255,0.08)] transition-all duration-300 border border-glass-border cursor-pointer group">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[rgba(255,46,136,0.1)] border border-primary-pink/30 shrink-0 shadow-[0_0_15px_rgba(255,46,136,0.2)] group-hover:scale-110 transition-transform">
                                        <Bell className="w-4 h-4 text-primary-pink" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-text-primary font-medium truncate drop-shadow-sm">{notification.message}</p>
                                        <p className="text-xs text-text-secondary mt-1">{notification.time || "Just now"}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    
                    {/* <button className="w-full mt-6 py-3.5 rounded-full bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-text-primary font-bold tracking-wide hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] transition-all duration-300 border border-glass-border">
                        View All Notifications
                    </button> */}
                </div>
            </div>
        </div>
    );
};

export default Home;